import { useState } from "react"

import { ApiError } from "@/api/client"
import {
  useAdminTopUpRequests,
  useUpdateTopUpRequest,
  type TopUpRequest,
} from "@/api/hooks/requests"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"

function formatDate(value: string | null): string {
  if (!value) return "—"
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString()
}

function formatAmount(amount: string | number, currency: string): string {
  const n = typeof amount === "number" ? amount : Number(amount)
  if (!Number.isFinite(n)) return `${currency} ${String(amount)}`
  const text = Number.isInteger(n) ? String(n) : n.toFixed(2)
  return `${currency} ${text}`
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === "approved"
      ? "bg-[#1e4d3e] text-[#4ade80]"
      : status === "cancelled"
        ? "bg-[#4a2a2a] text-[#f87171]"
        : "bg-[#2d3a4a] text-[#93c5fd]"
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${tone}`}
    >
      {status}
    </span>
  )
}

function TopUpRow({
  row,
  busyId,
  onApprove,
  onCancel,
}: {
  row: TopUpRequest
  busyId: number | null
  onApprove: (id: number) => void
  onCancel: (id: number) => void
}) {
  const pending = row.status === "pending"
  const busy = busyId === row.id

  return (
    <tr className="border-b border-[#555555] hover:bg-[#333]">
      <td className="px-4 py-3">
        <div className="font-semibold text-[#e0e0e0]">
          {row.requester?.username ?? `User #${row.requester_id}`}
        </div>
        <div className="mt-1 text-xs text-[#888]">{row.requester?.role}</div>
      </td>
      <td className="px-4 py-3">
        <div className="font-semibold text-[#e0e0e0]">{row.advertiser_name}</div>
        <div className="mt-1 font-mono text-xs text-[#b0b0b0]">
          ID: {row.advertiser_id}
        </div>
      </td>
      <td className="px-4 py-3 text-[#e0e0e0]">
        {formatAmount(row.amount, row.currency)}
      </td>
      <td className="px-4 py-3">
        <StatusPill status={row.status} />
      </td>
      <td className="px-4 py-3 text-[#b0b0b0]">{formatDate(row.created_at)}</td>
      <td className="px-4 py-3">
        {pending ? (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              disabled={busy}
              onClick={() => onApprove(row.id)}
              className="bg-[#3d8b5c] text-white hover:bg-[#337350]"
            >
              Approve
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={busy}
              onClick={() => onCancel(row.id)}
              className="bg-[#7f1d1d] text-white hover:bg-[#991b1b]"
            >
              Cancel
            </Button>
          </div>
        ) : (
          <span className="text-xs text-[#888]">
            {row.reviewer?.username
              ? `by ${row.reviewer.username}`
              : "—"}
          </span>
        )}
      </td>
    </tr>
  )
}

export default function TopUpRequestsTab() {
  const query = useAdminTopUpRequests()
  const updateMutation = useUpdateTopUpRequest()
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)

  const act = async (id: number, status: "approved" | "cancelled") => {
    setError(null)
    setBusyId(id)
    try {
      await updateMutation.mutateAsync({ id, status })
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Action failed",
      )
    } finally {
      setBusyId(null)
    }
  }

  const rows = query.data?.data ?? []

  return (
    <div className="space-y-4">
      {error ? (
        <Alert
          variant="destructive"
          className="border-[#663333] bg-[#4a2a2a] text-[#f87171]"
        >
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {query.isLoading ? (
        <p className="py-8 text-center text-[#b0b0b0]">Loading...</p>
      ) : query.isError ? (
        <p className="py-8 text-center text-[#f87171]">Failed to load requests</p>
      ) : rows.length === 0 ? (
        <p className="py-8 text-center text-[#888]">No top-up requests yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-[#555555]">
          <table className="w-full min-w-[900px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-[#555555] bg-[#444444] text-left text-xs font-semibold uppercase tracking-wide text-[#b0b0b0]">
                <th className="px-4 py-3">Buyer</th>
                <th className="px-4 py-3">Account</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Submitted</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-[#3a3a3a]">
              {rows.map((row) => (
                <TopUpRow
                  key={row.id}
                  row={row}
                  busyId={busyId}
                  onApprove={(id) => void act(id, "approved")}
                  onCancel={(id) => void act(id, "cancelled")}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
